import { useCallback, useRef } from 'react'
import * as React from 'react'
import styled from 'styled-components'
import { rem } from 'polished'
import clsx from 'clsx'
import CircleCheckboxInput from 'LuxKit/Checkbox/CircleCheckboxInput'
import RadioButton from 'LuxKit/Radio/RadioButton'
import TextLoadingBox from 'LuxKit/Typography/TextLoadingBox'
import LineInfoCircleIcon from 'LuxKit/Icons/line/LineInfoCircleIcon'
import IconButton from 'LuxKit/Button/IconButton'
import LoadingBox from 'components/Common/Loading/LoadingBox'
import noop from 'lib/function/noop'
import { canOpenLocationInfo } from './CardsList'
import type { CardOption, CardCategory } from './CardsList'
import LockedOptionsList from './LockedOptionsList'
import { SelectableCard } from './SelectableCard'
import { useCardSelection } from './useCardSelection'
import ImageCard from './ImageCard'
import { imageCardFrameStyles } from './ImageCardFrame'
import {
  OptionDescription,
  OptionTitle,
  StreamingFrame,
  StreamingSkeletonCard,
  TextContent,
  TitleRow,
  TrailingControl,
} from './CardListStyles'
import StreamingCardContainer from './StreamingCardContainer'

const ContentArea = styled.div`
  display: flex;
  align-items: center;
  gap: ${rem(12)};
  flex: 1;
  min-width: 0;
  padding: ${rem(12)} ${rem(16)};

  @container chat-card-list (max-width: 480px) {
    align-items: flex-start;
    gap: ${rem(8)};
    padding: ${rem(10)} ${rem(12)};
  }
`

const StreamingImageSkeleton = styled(LoadingBox)`
  ${imageCardFrameStyles}
`

interface Props {
  options: Array<CardOption>
  onSelectionChange: (selectedTitles: Array<string>) => void
  select: 'single' | 'multiple'
  disabled?: boolean
  selection?: Array<string>
  categories: 'locations' | 'places'
  onInfoClick?: (option: CardOption, category: CardCategory) => void
  isStreaming?: boolean
}

function ImageCardsList({
  options,
  onSelectionChange,
  select,
  disabled = false,
  selection,
  categories,
  onInfoClick = noop,
  isStreaming = false,
}: Props) {
  const { selectedIndices, optionClickHandlers } = useCardSelection({
    options,
    selection,
    disabled,
    select,
    onSelectionChange,
  })

  const onInfoClickRef = useRef(onInfoClick)
  onInfoClickRef.current = onInfoClick

  const getOptionForEvent = useCallback((optionIndex: string | undefined) => {
    const indexStr = optionIndex
    if (indexStr === undefined) return
    const index = parseInt(indexStr, 10)
    if (isNaN(index)) return
    return options[index]
  }, [options])

  const handleInfoClick = useCallback((event: React.MouseEvent<HTMLButtonElement>) => {
    event.stopPropagation()
    const option = getOptionForEvent(event.currentTarget.dataset.optionIndex)
    if (option && canOpenLocationInfo(option, categories)) {
      onInfoClickRef.current(option, categories)
    }
  }, [categories, getOptionForEvent])

  const handleSelectionClick = useCallback((event: React.MouseEvent<HTMLInputElement>) => {
    event.stopPropagation()
  }, [])

  const handleSelectionChange = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    const indexStr = event.currentTarget.dataset.optionIndex
    if (indexStr === undefined) return
    const index = parseInt(indexStr, 10)
    if (isNaN(index)) return
    optionClickHandlers[index]?.()
  }, [optionClickHandlers])

  const getOptionKey = useCallback((option: CardOption, index: number) => `${option.title}-${index}`, [])

  const renderOption = useCallback(({ option, index, isSelected }: { option: CardOption; index: number; isSelected: boolean }) => {
    let selectionControl: React.ReactNode
    const canOpenInfo = canOpenLocationInfo(option, categories)

    if (select === 'multiple') {
      selectionControl = (
        <CircleCheckboxInput
          checked={isSelected}
          disabled={disabled}
          data-option-index={index}
          onClick={handleSelectionClick}
          onChange={handleSelectionChange}
        />
      )
    } else {
      selectionControl = (
        <RadioButton
          checked={isSelected}
          disabled={disabled}
          data-option-index={index}
          onClick={handleSelectionClick}
          onChange={handleSelectionChange}
        />
      )
    }

    return (
      <StreamingCardContainer isStreaming={isStreaming}>
        <SelectableCard
          role={select === 'single' ? 'radio' : 'checkbox'}
          aria-checked={isSelected}
          disabled={disabled}
          onClick={optionClickHandlers[index]}
          className={clsx('with-image', { selected: isSelected })}
        >
          <ImageCard
            option={option}
            category={categories}
            optionIndex={index}
            onClick={canOpenInfo ? handleInfoClick : undefined}
          />
          <ContentArea>
            <TextContent direction="vertical" gap={2} fullWidth>
              <TitleRow direction="horizontal" gap={4} verticalAlign="center">
                <OptionTitle variant="medium" colour="normal-contrast" weight="bold">
                  {option.title}
                </OptionTitle>
                {canOpenInfo && (
                  <IconButton
                    kind="tertiary"
                    size="small"
                    data-option-index={index}
                    onClick={handleInfoClick}
                  >
                    <LineInfoCircleIcon />
                  </IconButton>
                )}
              </TitleRow>
              {option.description && (
                <OptionDescription variant="medium" colour="light-foreground-alt">
                  {option.description}
                </OptionDescription>
              )}
            </TextContent>
            <TrailingControl>{selectionControl}</TrailingControl>
          </ContentArea>
        </SelectableCard>
      </StreamingCardContainer>
    )
  }, [categories, disabled, handleInfoClick, handleSelectionChange, handleSelectionClick, isStreaming, optionClickHandlers, select])

  if (isStreaming && options.length === 0) {
    return (
      <StreamingFrame aria-busy="true" aria-live="polite" data-testid="image-cards-list-streaming-frame">
        <StreamingSkeletonCard className="with-image" data-testid="image-cards-list-streaming-skeleton">
          <StreamingImageSkeleton />
          <ContentArea>
            <TextContent direction="vertical" gap={8} fullWidth>
              <TextLoadingBox typography="body-medium" width="42%" />
              <TextLoadingBox typography="body-medium" width="76%" />
            </TextContent>
          </ContentArea>
        </StreamingSkeletonCard>
      </StreamingFrame>
    )
  }

  return (
    <LockedOptionsList
      options={options}
      disabled={disabled}
      selectedIndices={selectedIndices}
      getOptionKey={getOptionKey}
      renderOption={renderOption}
    />
  )
}

export default ImageCardsList
